import { getContentstackImageURL } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import type { HeroCarouselSlideProps } from 'src/components/organisms/HeroCarousel/HeroCarousel';
import type { HeroCarouselSectionProps } from 'src/components/sections/HeroCarouselSection/HeroCarouselSection';
import type { ValidationError } from 'src/content/types';
import type { SectionRenderer } from 'src/types/Section';
import type { Transformer } from 'src/types/Transformer';
import type { HeroCarouselSectionContent } from './types';

/* istanbul ignore next */
export const renderer: SectionRenderer<HeroCarouselSectionProps> =
  dynamic<HeroCarouselSectionProps>(() =>
    import('src/components/sections/HeroCarouselSection/HeroCarouselSection').then(
      (mod) => mod.HeroCarouselSection,
    ),
  );

export const transformer: Transformer<HeroCarouselSectionContent, HeroCarouselSectionProps> = ({
  _metadata,
  items,
  tracking_title,
}) => {
  const errors: ValidationError[] = [];
  const slides: HeroCarouselSlideProps[] = [];

  (items || []).forEach((item) => {
    const slide = item.hero_carousel_item?.[0];

    if (!slide) {
      return;
    }

    const buttonHref = slide.button_reference?.[0]?.url;

    if (!buttonHref) {
      errors.push({
        message: `Missing referenced page for hero carousel slide (uid: ${slide.uid}). Check that the linked page is published.`,
      });

      return;
    }

    slides.push({
      alt: slide.image.description || slide.heading_line_1,
      buttonHref,
      buttonText: slide.button_text || '',
      headingLine1: slide.heading_line_1,
      headingLine2: slide.heading_line_2,
      headingPosition: slide.heading_position,
      // Serve resized, webp-optimized crops sized to the hero's rendered box rather than the raw
      // Contentstack original (which can be many MB and would wreck LCP for this LCP element):
      // a wide 3:1 crop on desktop and a tall 3:4 crop on mobile.
      image: getContentstackImageURL(slide.image.url, 1440, { aspectRatio: '3:1' }),
      imageMobile: getContentstackImageURL(slide.image.url, 768, { aspectRatio: '3:4' }),
      paragraph: slide.paragraph || '',
      termsAndConditions: slide.terms_and_conditions,
    });
  });

  return {
    uid: _metadata.uid,
    props: {
      items: slides,
      trackingTitle: tracking_title,
    },
    errors,
  };
};
